import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

// Batch delay in ms - accumulate updates before processing
const BATCH_DELAY_MS = 1000;

/**
 * Custom hook for real-time contact updates with batched processing
 * Only use this when actively viewing the Contacts page
 * @param {function} onContactUpdate - Callback when a contact is updated
 * @param {function} onContactInsert - Callback when a new contact is created
 * @param {function} onContactDelete - Callback when a contact is deleted
 * @param {boolean} enabled - Whether the subscription is enabled
 */
export const useRealtimeContacts = (
  onContactUpdate,
  onContactInsert = null,
  onContactDelete = null,
  enabled = true
) => {
  // Refs for batching updates
  const pendingInserts = useRef(new Map());
  const pendingUpdates = useRef(new Map());
  const pendingDeletes = useRef(new Set());
  const batchTimeout = useRef(null);

  // Process all pending events in a single batch
  const processBatch = useCallback(async () => {
    const inserts = new Map(pendingInserts.current);
    const updates = new Map(pendingUpdates.current);
    const deletes = new Set(pendingDeletes.current);

    pendingInserts.current.clear();
    pendingUpdates.current.clear();
    pendingDeletes.current.clear();

    // Process deletes immediately (no fetch needed)
    if (deletes.size > 0 && onContactDelete) {
      deletes.forEach((id) => onContactDelete(id));
    }

    // Combine inserts and updates for a single batch fetch
    const allIds = [...inserts.keys(), ...updates.keys()];
    if (allIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          *,
          clients (
            id,
            name
          )
        `)
        .in('id', allIds);

      if (error) {
        console.error('Realtime: Error fetching contact batch:', error);
        return;
      }

      if (data) {
        data.forEach((contact) => {
          if (inserts.has(contact.id) && onContactInsert) {
            onContactInsert(contact);
          } else if (updates.has(contact.id) && onContactUpdate) {
            onContactUpdate(contact);
          }
        });
      }
    } catch (error) {
      console.error('Realtime: Contact batch fetch failed:', error);
    }
  }, [onContactUpdate, onContactInsert, onContactDelete]);

  // Queue an event for batched processing
  const queueEvent = useCallback(
    (payload) => {
      if (payload.eventType === 'INSERT' && payload.new && onContactInsert) {
        pendingInserts.current.set(payload.new.id, payload);
      } else if (payload.eventType === 'UPDATE' && payload.new && onContactUpdate) {
        pendingUpdates.current.set(payload.new.id, payload);
      } else if (payload.eventType === 'DELETE' && payload.old && onContactDelete) {
        pendingDeletes.current.add(payload.old.id);
      }

      // Reset the batch timer
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(processBatch, BATCH_DELAY_MS);
    },
    [processBatch, onContactUpdate, onContactInsert, onContactDelete],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Subscribe to all changes on the contacts table
    const channel = supabase
      .channel('contacts-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'contacts'
        },
        queueEvent
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Realtime: Failed to subscribe to contacts');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      supabase.removeChannel(channel);
    };
  }, [enabled, queueEvent]);
};